/**
 * EditableMarker - Marcador arrastrable para edición de coordenadas
 * V16 - Drag marker functionality
 * 
 * Extiende InfrastructureMarker añadiendo capacidad de arrastre
 * cuando el modo edición está activo.
 */

import React, { memo, useCallback, useRef, useEffect, useMemo } from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import type { Marker as LeafletMarker, DragEndEvent, LatLng } from 'leaflet';
import type { NormalizedInfrastructure, InfrastructureType } from '../../../types/infrastructure';
import { toWGS84, toUTM30, formatDragFeedback } from '../../../lib/leaflet-crs';
import { getMarkerConfig } from '../../../constants/markerConfig';
import { InfrastructurePopup } from '../InfrastructurePopup';
import '../infrastructure.css';

// =============================================================================
// TIPOS
// =============================================================================

export interface EditableMarkerProps {
  /** Infraestructura a mostrar */
  infrastructure: NormalizedInfrastructure;
  /** Si está en modo edición (arrastrable) */
  isEditable: boolean;
  /** Si este marcador específico está seleccionado */
  isSelected?: boolean;
  /** Si tiene cambios pendientes */
  hasChanges?: boolean;
  /** Coordenadas actuales (pueden ser diferentes a las originales si hay edición) */
  currentCoords?: { x: number; y: number };
  /** Callback al hacer click */
  onClick?: (infra: NormalizedInfrastructure) => void;
  /** Callback al terminar de arrastrar */
  onDragEnd?: (infra: NormalizedInfrastructure, newX: number, newY: number) => void;
  /** Callback al empezar a arrastrar */
  onDragStart?: (infra: NormalizedInfrastructure) => void;
}

// =============================================================================
// CONSTANTES
// =============================================================================

const MARKER_SIZE = { width: 32, height: 42 };

/** Cache de iconos editables */
const editableIconCache = new Map<string, L.DivIcon>();

// =============================================================================
// UTILIDADES
// =============================================================================

/**
 * Crea icono para marcador editable con indicadores visuales de estado
 */
function createEditableIcon(
  type: InfrastructureType,
  isEditable: boolean,
  isSelected: boolean,
  hasChanges: boolean
): L.DivIcon {
  const config = getMarkerConfig(type);
  const cacheKey = `${type}-${isEditable}-${isSelected}-${hasChanges}`;
  
  // Solo cachear estados estáticos
  if (!isSelected && !hasChanges && editableIconCache.has(cacheKey)) {
    return editableIconCache.get(cacheKey)!;
  }
  
  // Estilos según estado
  let borderColor = '#262624';
  let glowFilter = '';
  let cursor = 'pointer';
  
  if (isSelected) {
    borderColor = '#A3E635'; // Lime - seleccionado (solo borde, sin glow — regla GLOW-AUDIT)
    glowFilter = ''; // Sin drop-shadow verde: el borde lime ya indica selección
  } else if (hasChanges) {
    borderColor = '#F59E0B'; // Amber - editado
    glowFilter = 'drop-shadow(0 0 4px #F59E0B)';
  }
  
  if (isEditable) {
    cursor = 'grab';
  }
  
  // Indicador de modo edición (pequeño lápiz)
  const editIndicator = isEditable ? `
    <div style="
      position: absolute;
      top: -4px;
      right: -4px;
      width: 14px;
      height: 14px;
      background: #262624;
      border: 1px solid ${borderColor};
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 8px;
    ">✏️</div>
  ` : '';
  
  // Indicador de cambios pendientes
  const changesIndicator = hasChanges ? `
    <div style="
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 8px;
      height: 8px;
      background: #F59E0B;
      border-radius: 50%;
      border: 1px solid #262624;
    "></div>
  ` : '';
  
  const html = `
    <div class="ptel-marker-container ptel-marker-editable" style="
      width: ${MARKER_SIZE.width}px;
      height: ${MARKER_SIZE.height}px;
      position: relative;
      cursor: ${cursor};
      filter: ${glowFilter};
    ">
      <svg viewBox="0 0 32 42" width="${MARKER_SIZE.width}" height="${MARKER_SIZE.height}">
        <path 
          d="M16 0C7.163 0 0 7.163 0 16c0 8.837 16 26 16 26s16-17.163 16-26C32 7.163 24.837 0 16 0z" 
          fill="${config.color}"
          stroke="${borderColor}"
          stroke-width="${isSelected || hasChanges ? 2 : 1}"
        />
        <circle cx="16" cy="16" r="10" fill="#262624" fill-opacity="0.2"/>
      </svg>
      <span style="
        position: absolute;
        top: 8px;
        left: 50%;
        transform: translateX(-50%);
        font-size: 16px;
        line-height: 1;
      ">${config.icon}</span>
      ${editIndicator}
      ${changesIndicator}
    </div>
  `;
  
  const divIcon = L.divIcon({
    html,
    className: 'ptel-infrastructure-marker ptel-editable-marker',
    iconSize: [MARKER_SIZE.width, MARKER_SIZE.height],
    iconAnchor: [MARKER_SIZE.width / 2, MARKER_SIZE.height],
    popupAnchor: [0, -MARKER_SIZE.height + 4],
  });
  
  // Solo cachear estados sin selección/cambios
  if (!isSelected && !hasChanges) {
    editableIconCache.set(cacheKey, divIcon);
  }
  
  return divIcon;
}

/**
 * Limpia la cache de iconos editables
 */
export function clearEditableIconCache(): void {
  editableIconCache.clear();
}

// =============================================================================
// COMPONENTE
// =============================================================================

export const EditableMarker = memo<EditableMarkerProps>(function EditableMarker({
  infrastructure,
  isEditable,
  isSelected = false,
  hasChanges = false,
  currentCoords,
  onClick,
  onDragEnd,
  onDragStart,
}) {
  const markerRef = useRef<LeafletMarker>(null);
  const rafRef = useRef<number>(0);
  const dragTooltipRef = useRef<L.Tooltip | null>(null);

  // Coordenadas originales para cálculo durante arrastre
  const originalUTM = useMemo(
    () => ({ x: infrastructure.x, y: infrastructure.y }),
    [infrastructure.x, infrastructure.y]
  );

  // Coordenadas a usar (editadas o originales)
  const coords = currentCoords ?? { x: infrastructure.x, y: infrastructure.y };
  
  // Convertir UTM a WGS84 para Leaflet
  const position = useMemo<[number, number]>(() => {
    const [lat, lng] = toWGS84(coords.x, coords.y);
    return [lat, lng];
  }, [coords.x, coords.y]);

  // Crear icono según estado
  const icon = useMemo(
    () => createEditableIcon(infrastructure.type, isEditable, isSelected, hasChanges),
    [infrastructure.type, isEditable, isSelected, hasChanges]
  );

  // Handler click
  const handleClick = useCallback(() => {
    onClick?.(infrastructure);
  }, [infrastructure, onClick]);

  // Handler drag start — crea tooltip imperativo
  const handleDragStart = useCallback(() => {
    onDragStart?.(infrastructure);
    // Crear tooltip nativo Leaflet (sin React state)
    if (markerRef.current) {
      const tooltip = L.tooltip({
        permanent: true,
        direction: 'top',
        offset: [0, -48],
        className: 'ptel-drag-feedback-tooltip',
      });
      tooltip.setContent('·');
      tooltip.setLatLng(markerRef.current.getLatLng());
      markerRef.current.bindTooltip(tooltip);
      dragTooltipRef.current = tooltip;
      const el = markerRef.current.getElement();
      if (el) el.style.cursor = 'grabbing';
    }
  }, [infrastructure, onDragStart]);

  // Handler drag (continuous) — throttled via rAF, 100% imperativo
  const handleDrag = useCallback(() => {
    if (rafRef.current) return; // ya hay un frame pendiente
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const marker = markerRef.current;
      const tooltip = dragTooltipRef.current;
      if (!marker || !tooltip) return;
      const pos = marker.getLatLng();
      const [curX, curY] = toUTM30(pos.lat, pos.lng);
      const text = formatDragFeedback(originalUTM.x, originalUTM.y, curX, curY);
      tooltip.setContent(text);
      tooltip.setLatLng(pos);
    });
  }, [originalUTM]);

  // Handler drag end — limpia tooltip imperativo
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    // Cancelar rAF pendiente
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    // Destruir tooltip imperativo
    if (markerRef.current && dragTooltipRef.current) {
      markerRef.current.unbindTooltip();
      dragTooltipRef.current = null;
    }

    const marker = event.target as LeafletMarker;
    const newPosition: LatLng = marker.getLatLng();
    
    // Restaurar cursor
    if (markerRef.current) {
      const el = markerRef.current.getElement();
      if (el) el.style.cursor = isEditable ? 'grab' : 'pointer';
    }
    
    // Convertir de vuelta a UTM
    const [newX, newY] = toUTM30(newPosition.lat, newPosition.lng);
    
    onDragEnd?.(infrastructure, newX, newY);
  }, [infrastructure, onDragEnd, isEditable]);

  // Event handlers combinados
  const eventHandlers = useMemo(() => ({
    click: handleClick,
    dragstart: handleDragStart,
    drag: handleDrag,
    dragend: handleDragEnd,
  }), [handleClick, handleDragStart, handleDrag, handleDragEnd]);

  // Z-index para marcadores seleccionados
  useEffect(() => {
    if (markerRef.current) {
      const el = markerRef.current.getElement();
      if (el) {
        el.style.zIndex = isSelected ? '1000' : hasChanges ? '999' : '';
      }
    }
  }, [isSelected, hasChanges]);

  // Cleanup en desmontaje
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      dragTooltipRef.current = null;
    };
  }, []);

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={icon}
      draggable={isEditable}
      eventHandlers={eventHandlers}
      alt={`${infrastructure.name} - ${isEditable ? 'Arrastrar para mover' : 'Click para detalles'}`}
      title={infrastructure.name}
    >
      <InfrastructurePopup
        infrastructure={{
          ...infrastructure,
          // Mostrar coordenadas actuales en popup
          x: coords.x,
          y: coords.y,
        }}
        showUTM={true}
        showScore={true}
        showSource={true}
      />
    </Marker>
  );
});

export default EditableMarker;
